import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TokenUsageProviderEntry } from 'features/usage/services/usage-service';
import { UsageDay } from 'features/usage/utils/build-usage-view-model';
import { formatNumber } from 'features/usage/utils/usage-format';

const providerPalette = [
  'hsl(174 72% 40%)',
  'hsl(262 52% 68%)',
  'hsl(38 92% 50%)',
  'hsl(12 80% 65%)',
  'hsl(210 80% 55%)',
  'hsl(330 70% 55%)',
] as const;

function formatAxisTokens(value: number) {
  if (value >= 1_000_000) return `${Number((value / 1_000_000).toFixed(1))}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return String(value);
}

function ChartHoverCard({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
      <div className="mb-2 text-xs font-medium text-foreground">{title}</div>
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-6 text-xs">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-mono text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyUsageTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ payload: UsageDay }>;
  label?: string;
}) {
  const { t } = useTranslation();
  const day = payload?.[0]?.payload;
  if (!active || !day) return null;

  return (
    <ChartHoverCard
      title={format(parseISO(day.date || String(label)), 'MMM dd, yyyy')}
      rows={[
        { label: t('TOTAL_TOKENS'), value: formatNumber(day.total_tokens) },
        { label: t('INPUT'), value: formatNumber(day.input_tokens) },
        { label: t('OUTPUT'), value: formatNumber(day.output_tokens) },
        { label: t('TURNS'), value: formatNumber(day.turn_count) },
      ]}
    />
  );
}

function ShareTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number }>;
}) {
  const point = payload?.[0];
  if (!active || !point) return null;

  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
      <div className="flex items-center justify-between gap-6 text-xs">
        <span className="text-muted-foreground">{point.name}</span>
        <span className="font-mono text-foreground">{formatNumber(Number(point.value ?? 0))}</span>
      </div>
    </div>
  );
}

export function UsageShareChart({
  items,
}: {
  items: Array<{ name: string; value: number; display?: string }>;
}) {
  const data = items.map((item, index) => ({
    ...item,
    fill: providerPalette[index % providerPalette.length],
  }));

  return (
    <div className="grid gap-6 md:grid-cols-[7rem_minmax(0,1fr)] md:items-start">
      <PieChart width={112} height={112} className="shrink-0">
        <Tooltip
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 20 }}
          content={<ShareTooltip />}
        />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={32}
          outerRadius={48}
          stroke="none"
          startAngle={90}
          endAngle={-270}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} />
          ))}
        </Pie>
      </PieChart>
      <div className="flex-1 space-y-3">
        {data.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: entry.fill }} />
              {entry.name}
            </div>
            <span className="font-mono text-sm text-foreground">
              {entry.display ?? formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UsageProviderChart({ providers }: { providers: TokenUsageProviderEntry[] }) {
  const { t } = useTranslation();
  const total = providers.reduce((sum, provider) => sum + provider.model_call_count, 0);

  return (
    <UsageShareChart
      items={providers.map((provider) => {
        const share = total ? (provider.model_call_count / total) * 100 : 0;
        return {
          name: provider.provider,
          value: provider.model_call_count,
          display: `${formatNumber(provider.model_call_count)} ${t('CALLS')} · ${share.toFixed(1)}%`,
        };
      })}
    />
  );
}

export function UsageDailyChart({ days }: { days: UsageDay[] }) {
  return (
    <div className="h-60 w-full sm:h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={days} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
          <XAxis
            dataKey="date"
            tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
            tickMargin={8}
            minTickGap={28}
          />
          <YAxis
            tickFormatter={(value) => formatAxisTokens(Number(value))}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ zIndex: 20 }}
            content={<DailyUsageTooltip />}
          />
          <Area
            type="monotone"
            dataKey="total_tokens"
            stroke="hsl(var(--usage-chart-1))"
            fill="none"
            strokeWidth={2}
            dot={{ r: 3, fill: 'hsl(var(--usage-chart-1))', strokeWidth: 0 }}
            activeDot={{ r: 5, fill: 'hsl(var(--usage-chart-1))', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
